import { useAuth } from '../../contexts/AuthContext';
import { ANALYSIS_OPTIONS } from '../../../shared/types';

/* Onglet « Utilisation » des paramètres — extrait tel quel de
 * AccountSettingsPage.tsx (markup/classes identiques). */

export interface UsageStats {
  monthlyAnalyses: number;
  totalAnalyses: number;
  byType: Record<string, number>;
  freeAnalysesUsed: number;
  freeAnalysesLimit: number;
  creditBalance: number;
  plan: string;
}

export default function UsageTabContent({
  usageStats,
  usageLoading,
  planLabel,
}: {
  usageStats: UsageStats | null;
  usageLoading: boolean;
  planLabel: string;
}) {
  const { features } = useAuth();
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-900">Utilisation</h2>

      {usageLoading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Chargement...</div>
      ) : usageStats ? (
        <>
          {/* Monthly gauge */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Ce mois-ci</h3>

            {features.unlimited ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Analyses effectuées</span>
                  <span className="font-medium text-slate-900 flex items-center gap-1.5">
                    {usageStats.monthlyAnalyses}
                    <span className="text-slate-400">/</span>
                    <span className="text-emerald-600 text-base leading-none">∞</span>
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full w-full rounded-full bg-emerald-400"
                    style={{ boxShadow: '0 0 8px 1px rgba(16, 185, 129, 0.55), 0 0 16px 2px rgba(16, 185, 129, 0.25)' }}
                  />
                </div>
                <p className="text-xs text-slate-500">Analyses illimitées avec votre plan {planLabel}</p>
              </div>
            ) : usageStats.freeAnalysesLimit === 0 ? (
              // Modèle 100% payant : le plan `free` ne comprend aucune analyse incluse.
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900">Aucune analyse incluse</p>
                <p className="text-xs text-slate-500">
                  Votre plan ne comprend aucune analyse incluse. Abonnez-vous (Pro ou Premium) ou achetez un bundle pour lancer des analyses.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">
                    {usageStats.plan === 'beta' ? 'Analyses Beta Partner' : 'Analyses gratuites'}
                  </span>
                  <span className="font-medium text-slate-900">
                    {usageStats.freeAnalysesUsed} / {usageStats.freeAnalysesLimit}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      usageStats.freeAnalysesUsed >= usageStats.freeAnalysesLimit
                        ? 'bg-red-500'
                        : usageStats.plan === 'beta'
                          ? 'bg-emerald-500'
                          : 'bg-violet-500'
                    }`}
                    style={{ width: `${Math.min(100, (usageStats.freeAnalysesUsed / usageStats.freeAnalysesLimit) * 100)}%` }}
                  />
                </div>
                {usageStats.freeAnalysesUsed >= usageStats.freeAnalysesLimit && (
                  <p className="text-xs text-orange-600">
                    {usageStats.plan === 'beta'
                      ? 'Quota Beta Partner atteint. Passez au Pro pour continuer.'
                      : 'Quota atteint. Passez au Pro pour des analyses illimitées.'}
                  </p>
                )}
              </div>
            )}

            {usageStats.creditBalance > 0 && (
              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Crédits à l'unité</span>
                  <span className="font-medium text-slate-900">{usageStats.creditBalance}</span>
                </div>
              </div>
            )}
          </div>

          {/* By type breakdown */}
          {Object.keys(usageStats.byType).length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Par type d'analyse</h3>
              <div className="space-y-2">
                {Object.entries(usageStats.byType)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const option = ANALYSIS_OPTIONS.find(o => o.key === type);
                    return (
                      <div key={type} className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">
                          {option?.icon} {option?.label ?? type}
                        </span>
                        <span className="font-medium text-slate-900">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Total (depuis l'inscription)</span>
              <span className="font-semibold text-slate-900">{usageStats.totalAnalyses} analyse{usageStats.totalAnalyses !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-500 py-8 text-center">
          Impossible de charger les statistiques.
        </div>
      )}
    </div>
  );
}
